/**
 * customEval logic for the PLATE source type
 */
import {gql} from '@apollo/client';
import stringify from 'json-stable-stringify';

import {assert} from 'Asserts';
import {prependAOrAn} from 'BenchlingUtil';
import {getNonBatchContainerContents} from 'constants/SampleConstants';
import {PlateSchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeByTypename from 'graphql-client/ExcludeByTypename';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  GetDestinationPlateSchemaQuery,
  GetDestinationPlateSchemaQueryVariables,
} from 'notebook/integrations/samples/transfer-table-columns/__generated/GetDestinationPlateSchemaQuery';
import {GET_DESTINATION_PLATE_SCHEMA_QUERY} from 'notebook/integrations/samples/transfer-table-columns/DestinationPlateCustomEval';
import extractStorableId from 'notebook/integrations/samples/transfer-table-columns/extractStorableId';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {
  ExistingBioentityColumnDefinition,
  PlateColumnDefinition,
  PositionColumnDefinition,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import kindToTableType from 'notebook/structured-tables/util/kindToTableType';
import {
  CustomResolutionQueryType,
  InventoryTableSourceEntityFromSourcePlateQuery,
  InventoryTableValidateSourceFixedPlateQuery,
  InventoryTableValidateSourcePositionQuery,
  ServerResolutionResult,
  StringResolutionType,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {
  ArrayTypedValue,
  BaseTypedValueType,
  CustomEval,
  CustomEvalResult,
  TypedValue,
} from 'notebook/tables/TableConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {formatTypedValue, isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
  ResolveTypedValueToTypeFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {BioentityTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {
  InvalidPositionError,
  InvalidPositionFormatError,
  parseGridPosition,
  PositionOutOfBoundsError,
} from 'samples/SampleUtil';
import APIUtil from 'util/APIUtil';
import lruMemoize from 'util/lruMemoize';
import {assertNever} from 'util/Types';

import {GetPlateContents, GetPlateContentsVariables} from './__generated/GetPlateContents';
import extractPlateId from './extractPlateId';

type ContainerContent = ExtractByTypename<
  NonNullable<
    ExtractByTypename<GetPlateContents['plates'][number], 'PlateNode'>['cells'][number][number]
  >['container'],
  'ContainerNode'
>['contents'][number];

type ContainedItem = ExcludeByTypename<ContainerContent['containedItem'], 'BatchNode'>;

interface ContentWithoutBatches extends ContainerContent {
  containedItem: ContainedItem;
}

export const GET_PLATE_CONTENTS_QUERY = gql`
  query GetPlateContents($plateIds: [StrictString!]!) @skipAudit @engComponent(name: INVENTORY) {
    plates(ids: $plateIds) {
      ... on PlateNode {
        id
        name
        storableSchema {
          ... on PlateSchemaNode {
            id
            height
            plateSchemaType
            width
          }
        }
        cells {
          rowIndex
          columnIndex
          container {
            ... on ContainerNode {
              id
              contents {
                id
                containedItem {
                  ... on RegistryItemInterface {
                    id
                    name
                  }
                  ...InaccessibleInfoGraphQL
                }
              }
            }
            ...InaccessibleInfoGraphQL
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

// To mock our GraphQL call, we need to wrap it in a method.
// This is different from most of our GraphQL tests, where we mock the Apollo cache.
// Here, mocking the cache won't work, because our fetch policy is `'no-cache'`.
export const stubbableFunctions = {
  async doPlateSchemaQuery(plateId: string): Promise<GetDestinationPlateSchemaQuery> {
    return await doQueryWithoutCache<GetDestinationPlateSchemaQuery, GetDestinationPlateSchemaQueryVariables>(
      {query: GET_DESTINATION_PLATE_SCHEMA_QUERY},
      {
        variables: {plateIds: [plateId]},
      }
    );
  },

  async doQuery(plateId: string): Promise<GetPlateContents> {
    return await doQueryWithoutCache<GetPlateContents, GetPlateContentsVariables>(
      {query: GET_PLATE_CONTENTS_QUERY},
      {variables: {plateIds: [plateId]}}
    );
  },
};

type SourcePlateError =
  | {type: 'INACCESSIBLE'}
  | {type: 'INACCESSIBLE_WELL'}
  | {type: 'INVALID_SCHEMA'}
  | {type: 'INVALID_SCHEMA_TYPE'}
  | {
      type: 'OUT_OF_BOUNDS';
      plateName: string;
    }
  | {
      type: 'INVALID_POSITION_FORMAT';
    }
  | {type: 'WELL_IS_EMPTY'}
  | {type: 'WELL_HAS_MULTIPLE_CONTENTS'}
  | {type: 'WELL_CONTAINS_BATCH'};

function isError(
  maybeError: BioentityTypedValue | ArrayTypedValue | {error: SourcePlateError}
): maybeError is {error: SourcePlateError} {
  // eslint-disable-next-line benchlint/no-in-operator
  return 'error' in maybeError;
}

/**
 * Do data fetching for the Source Plate, Source Position (Source Well), and Source Entity columns.
 *
 * If successful, return a BioentityTypedValue representing the plate well's contents.
 *
 * If there is an error, return the error code.
 */
async function resolveSourcePlate(
  plateId: string,
  position: string,
  allowMultiContentsWell?: boolean,
  plateSchemaId?: string | null,
  plateSchemaType?: PlateSchemaType
): Promise<BioentityTypedValue | ArrayTypedValue | {error: SourcePlateError}> {
  const data = await stubbableFunctions.doQuery(plateId);

  const plate = data.plates[0];
  if (isInaccessible(plate)) {
    return {error: {type: 'INACCESSIBLE'}};
  }
  assert(plate.storableSchema.__typename === 'PlateSchemaNode', 'Expected a plate schema');
  if (plateSchemaType != null && plate.storableSchema.plateSchemaType !== plateSchemaType) {
    return {error: {type: 'INVALID_SCHEMA_TYPE'}};
  }
  if (plateSchemaId != null && plate.storableSchema.id !== plateSchemaId) {
    return {error: {type: 'INVALID_SCHEMA'}};
  }

  let rowIndex: number;
  let columnIndex: number;
  try {
    ({rowIndex, columnIndex} = parseGridPosition({
      position,
      gridHeight: plate.storableSchema.height,
      gridWidth: plate.storableSchema.width,
    }));
  } catch (e) {
    if (e instanceof InvalidPositionError) {
      if (e instanceof PositionOutOfBoundsError) {
        return {error: {type: 'OUT_OF_BOUNDS', plateName: plate.name}};
      } else if (e instanceof InvalidPositionFormatError) {
        return {error: {type: 'INVALID_POSITION_FORMAT'}};
      }
      throw e;
    } else {
      throw e;
    }
  }

  const gridCell = plate.cells[rowIndex][columnIndex];
  if (gridCell == null) {
    assert(
      // Only matrix plates can have a position with no grid cell at all.
      // If a fixed plate has no grid cell at a position,
      // that means the grid coordinates were invalid somehow.
      plate.storableSchema.plateSchemaType === PlateSchemaType.MATRIX_PLATE,
      'Invalid grid coordinates should have been caught by parseGridPosition'
    );
  } else if (isInaccessible(gridCell.container)) {
    return {error: {type: 'INACCESSIBLE_WELL'}};
  }
  const contents =
    gridCell && !isInaccessible(gridCell.container)
      ? getNonBatchContainerContents<ContainerContent, ContentWithoutBatches>(gridCell.container.contents)
      : [];

  if (contents.length === 0) {
    return {error: {type: 'WELL_IS_EMPTY'}};
  }

  // TODO(PLAN-1130) Better handling for inaccessible contents
  if (allowMultiContentsWell) {
    const containedItems = contents.map((content) => content.containedItem);
    return {
      type: BaseTypedValueType.ARRAY,
      rawValue: containedItems.map((item) => ({
        type: BaseTypedValueType.BIOENTITY,
        bioentitySummary: {
          id: getAccessibleOrInaccessibleId(item),
          name: isInaccessible(item) ? '(No access)' : item.name,
        },
      })),
    };
  }

  if (contents.length > 1) {
    return {error: {type: 'WELL_HAS_MULTIPLE_CONTENTS'}};
  }
  const [{containedItem}] = contents;

  // TODO(PLAN-1130) Better handling for inaccessible contents
  return {
    type: BaseTypedValueType.BIOENTITY,
    bioentitySummary: {
      id: getAccessibleOrInaccessibleId(containedItem),
      name: isInaccessible(containedItem) ? '(No access)' : containedItem.name,
    },
  };
}

export const SourcePlateResolver = {
  validateSourceFixedPlate: async (
    query: InventoryTableValidateSourceFixedPlateQuery
  ): Promise<ServerResolutionResult> => {
    const data = await stubbableFunctions.doPlateSchemaQuery(query.plateId);
    const plate = data.storables[0];
    if (isInaccessible(plate)) {
      return makeCustomResolutionResultFromTypedValue(null);
    }
    assert(plate.__typename === 'PlateNode', 'Expected a plate');
    assert(plate.storableSchema.__typename === 'PlateSchemaNode', 'Expected a plate schema');
    if (plate.storableSchema.plateSchemaType !== PlateSchemaType.FIXED_PLATE) {
      return {
        type: undefined,
        error: `Plate must be a fixed plate`,
        resolvedValue: undefined,
      };
    }
    const schemaId = plate.storableSchema.id;

    if (query.plateSchemaId == null || schemaId === query.plateSchemaId) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    assert(query.plateSchemaName != null, 'plateSchemaName should not be null if plateSchemaId is not');

    return {
      type: undefined,
      error: `Plate must be ${prependAOrAn(query.plateSchemaName)}`,
      resolvedValue: undefined,
    };
  },

  validateSourcePosition: async (
    query: InventoryTableValidateSourcePositionQuery
  ): Promise<ServerResolutionResult> => {
    const maybeError = await resolveSourcePlate(query.plateId, query.position, query.allowMultiContentsWell);
    if (!isError(maybeError)) {
      // Everything is OK
      return {
        type: StringResolutionType.CUSTOM,
        error: undefined,
        resolvedValue: {
          typedValue: null,
        },
      };
    }

    // There's an error. How should it be presented?
    const error = maybeError.error;
    switch (error.type) {
      case 'INACCESSIBLE':
        return makeCustomResolutionResultFromTypedValue(null);
      case 'INACCESSIBLE_WELL':
        return {
          type: undefined,
          error: 'Well is inaccessible',
          resolvedValue: undefined,
        };
      case 'INVALID_SCHEMA':
        return makeCustomResolutionResultFromTypedValue(null);
      case 'INVALID_SCHEMA_TYPE':
        return makeCustomResolutionResultFromTypedValue(null);
      case 'OUT_OF_BOUNDS':
        return {
          type: undefined,
          error: `${query.position} is not a valid position in ${error.plateName}.`,
          resolvedValue: undefined,
        };
      case 'WELL_IS_EMPTY':
        if (query.allowEmptyWell) {
          return makeCustomResolutionResultFromTypedValue(null);
        }
        return {
          type: undefined,
          error: 'Well must not be empty',
          resolvedValue: undefined,
        };

      case 'WELL_HAS_MULTIPLE_CONTENTS':
        return {
          type: undefined,
          error: 'Well has multiple contents',
          resolvedValue: undefined,
        };
      case 'WELL_CONTAINS_BATCH':
        return {
          type: undefined,
          error: 'Contents must be an entity, not a batch',
          resolvedValue: undefined,
        };
      case 'INVALID_POSITION_FORMAT':
        return {
          type: undefined,
          error: 'Position must be in format: A1, A01, or 1',
          resolvedValue: undefined,
        };
      default:
        throw new Error(`Unexpected error type ${assertNever(error).type}`);
    }
  },

  autopopulateSourceEntity: async (
    query: InventoryTableSourceEntityFromSourcePlateQuery
  ): Promise<ServerResolutionResult> => {
    const maybeBioentityTypedValue = await resolveSourcePlate(
      query.plateId,
      query.position,
      query.allowMultiContentsWell,
      query.plateSchemaId,
      query.plateSchemaType
    );
    // eslint-disable-next-line benchlint/no-in-operator
    if ('error' in maybeBioentityTypedValue) {
      // There's an error, so there's no Source Entity to autopopulate.
      // But don't return an error here - errors will be shown in the other columns.
      return {
        type: StringResolutionType.CUSTOM,
        error: undefined,
        resolvedValue: {
          typedValue: null,
        },
      };
    } else {
      return {
        type: StringResolutionType.CUSTOM,
        error: undefined,
        resolvedValue: {
          typedValue: maybeBioentityTypedValue,
        },
      };
    }
  },
};

export function sourcePlateCustomEval(
  typedValue: TypedValue,
  columnDefinition: PlateColumnDefinition,
  cellDependencies: [],
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn,
  resolveTypedValueToType: ResolveTypedValueToTypeFn
): CustomEvalResult {
  // TODO(BNCH-15744): customEval currently runs _before_ string resolution and this particular resolution
  // would like to operate on a post-resolved value, so we manually fire it off first.
  const {resolvedValue: stringResolvedValue} = resolveTypedValueToType(columnDefinition, typedValue, {
    tableType: kindToTableType(StructuredTableKind.INVENTORY, false),
  });
  if (stringResolvedValue.type !== BaseTypedValueType.STORABLES) {
    return {val: typedValue, error: null, locked: false};
  }

  const plateId = extractStorableId(stringResolvedValue);
  if (plateId == null || !APIUtil.isPlate(plateId)) {
    // TODO(BNCH-15744): the evaluator currently runs customEval before resolveTypedValue,
    // so by returning the original typed value here, we're falling back to the default validation logic
    // in resolveTypedValue
    return {val: typedValue, error: null, locked: false};
  }

  const {resolvedValue: customResolvedValue, error} = resolveFromResolutionQuery(
    stringResolvedValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_VALIDATE_SOURCE_FIXED_PLATE,
      type: ResolutionQueryType.CUSTOM,
      plateId,
      plateSchemaId: columnDefinition.plateSchemaId ?? null,
      plateSchemaName: columnDefinition.plateSchemaName ?? null,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: customResolvedValue, error, locked: false};
}

export const getSourcePositionCustomEval = lruMemoize(_getSourcePositionCustomEval, {max: 100}, (...args) =>
  stringify(args)
);

function _getSourcePositionCustomEval<CustomEvalOptions>({
  allowEmptyWell,
  allowMultiContentsWell,
}: {
  allowEmptyWell?: boolean;
  allowMultiContentsWell?: boolean;
} = {}): CustomEval<CustomEvalOptions> {
  return (
    typedValue: TypedValue,
    columnDefinition: PositionColumnDefinition,
    [plateTypedValue]: [TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ) => {
    if (isTypedValueEmpty(typedValue)) {
      return {val: typedValue, error: null, locked: false};
    }

    const position = formatTypedValue(typedValue);
    const plateId = extractPlateId(plateTypedValue);
    if (plateId == null) {
      return {val: {type: BaseTypedValueType.STRING, rawValue: position}, error: null, locked: false};
    }

    const {resolvedValue, error} = resolveFromResolutionQuery(
      {type: BaseTypedValueType.STRING, rawValue: position},
      {
        customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_VALIDATE_SOURCE_POSITION,
        plateId,
        position,
        ...(allowEmptyWell && {allowEmptyWell}),
        ...(allowMultiContentsWell != null && {allowMultiContentsWell}),
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {val: resolvedValue, error, locked: false};
  };
}

export const getSourceEntityCustomEval = lruMemoize(_getSourceEntityCustomEval, {max: 100}, (...args) =>
  stringify(args)
);

function _getSourceEntityCustomEval<CustomEvalOptions>({
  plateSchemaId,
  plateSchemaType,
  allowMultiContentsWell,
}: {
  plateSchemaId?: string | null;
  plateSchemaType?: PlateSchemaType;
  allowMultiContentsWell?: boolean;
} = {}): CustomEval<CustomEvalOptions> {
  return (
    typedValue: TypedValue,
    columnDefinition: ExistingBioentityColumnDefinition,
    [plateTypedValue, positionTypedValue]: [TypedValue | null, TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ) => {
    if (!positionTypedValue || isTypedValueEmpty(positionTypedValue)) {
      return {val: {type: BaseTypedValueType.STRING, rawValue: ''}, error: null, locked: false};
    }

    const position = formatTypedValue(positionTypedValue);
    const plateId = extractPlateId(plateTypedValue);
    if (plateId == null) {
      return {val: {type: BaseTypedValueType.STRING, rawValue: ''}, error: null, locked: false};
    }

    const {resolvedValue, error} = resolveFromResolutionQuery(
      typedValue,
      {
        customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_SOURCE_ENTITY_FROM_SOURCE_PLATE,
        plateId,
        position,
        ...(plateSchemaId != null && {plateSchemaId}),
        ...(plateSchemaType != null && {plateSchemaType}),
        ...(allowMultiContentsWell != null && {allowMultiContentsWell}),
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {val: resolvedValue, error, locked: false};
  };
}
